import { parseThemeBlock, type ThemeBlock } from "../shared/presentation";
import { boundedKeys, exactKeys, GameSpecValidationError, glyph, integer, record, text } from "../shared/validators";

export interface TargetRangeSpec {
  readonly version: 1;
  readonly kind: "target-range";
  readonly title: string;
  readonly seed: number;
  readonly theme: ThemeBlock;
  readonly params: {
    readonly rangeWidth: number;
    readonly rangeHeight: number;
    readonly targetCount: number;
    readonly targetRadius: number;
    readonly targetLifetimeSeconds: number;
    readonly projectileSpeed: number;
    readonly goalHits: number;
    readonly timeLimitSeconds: number;
    readonly waves?: number;
    /** Back-compat name for the attacking glyph; superseded by attackerGlyph when both are absent-safe defaults still apply. */
    readonly targetGlyph?: string;
    /** What's attacking -- flies in from offscreen toward the defender/protectees. */
    readonly attackerGlyph?: string;
    /** Who the player is; the defender stands at the range's foot, intercepting attackers before they reach the protectees. */
    readonly defenderGlyph?: string;
    /** Who's being protected -- a row of these stands behind the defender; losing all of them ends the round. */
    readonly protecteeGlyph?: string;
  };
}

/**
 * A defense game: whatever's attacking flies in from offscreen toward the
 * defender and the protectees behind them; aim-and-shoot before it arrives.
 * Covers archery/darts/shooting-gallery briefs AND two-actor attack briefs
 * ("dogfood attacks lions") -- the attacker, the defender, and what's being
 * protected are all distinct, castable roles now, not one static dot.
 */
export function parseTargetRangeSpec(root: Record<string, unknown>): TargetRangeSpec {
  exactKeys(root, ["version", "kind", "title", "seed", "theme", "params"], "game spec");
  if (root.kind !== "target-range") throw new GameSpecValidationError("unsupported game kind");

  const params = record(root.params, "params");
  boundedKeys(
    params,
    ["rangeWidth", "rangeHeight", "targetCount", "targetRadius", "targetLifetimeSeconds", "projectileSpeed", "goalHits", "timeLimitSeconds"],
    ["waves", "targetGlyph", "attackerGlyph", "defenderGlyph", "protecteeGlyph"],
    "params",
  );

  const goalHits = integer(params.goalHits, "params.goalHits", 3, 30);
  // The lower bound rises with goalHits so a valid spec can never require more hits than targets exist.
  const targetCount = integer(params.targetCount, "params.targetCount", Math.max(5, goalHits), 40);
  const waves = params.waves === undefined ? undefined : integer(params.waves, "params.waves", 1, 3);
  const targetGlyph = glyph(params.targetGlyph, "params.targetGlyph");
  const attackerGlyph = glyph(params.attackerGlyph, "params.attackerGlyph");
  const defenderGlyph = glyph(params.defenderGlyph, "params.defenderGlyph");
  const protecteeGlyph = glyph(params.protecteeGlyph, "params.protecteeGlyph");

  return {
    version: 1,
    kind: "target-range",
    title: text(root.title, "title", 1, 80),
    seed: integer(root.seed, "seed", 0, 4_294_967_295),
    theme: parseThemeBlock(root.theme),
    params: {
      rangeWidth: integer(params.rangeWidth, "params.rangeWidth", 480, 1_280),
      rangeHeight: integer(params.rangeHeight, "params.rangeHeight", 320, 800),
      targetCount,
      targetRadius: integer(params.targetRadius, "params.targetRadius", 24, 64),
      targetLifetimeSeconds: integer(params.targetLifetimeSeconds, "params.targetLifetimeSeconds", 2, 6),
      projectileSpeed: integer(params.projectileSpeed, "params.projectileSpeed", 500, 1_400),
      goalHits,
      timeLimitSeconds: integer(params.timeLimitSeconds, "params.timeLimitSeconds", 20, 180),
      ...(waves === undefined ? {} : { waves }),
      ...(targetGlyph === undefined ? {} : { targetGlyph }),
      ...(attackerGlyph === undefined ? {} : { attackerGlyph }),
      ...(defenderGlyph === undefined ? {} : { defenderGlyph }),
      ...(protecteeGlyph === undefined ? {} : { protecteeGlyph }),
    },
  };
}
